import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const MARITAL = ["Single", "Married", "Divorced", "Widowed"];

export default function Membership() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", address: "",
    date_of_birth: "", marital_status: "", salvation_testimony: "", previous_church: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/membership", f);
      setDone(true);
      toast.success("Welcome to the ETI family!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="membership-success">
        <p className="overline text-terracotta mb-4">Welcome home</p>
        <h1 className="font-heading font-black text-5xl tracking-tighter text-olive">You're family now.</h1>
        <p className="mt-6 text-ink/75 text-lg">
          Thank you, <span className="font-semibold">{f.full_name}</span>. Our team will reach out
          to walk you through the next steps of membership.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="membership-page">
      <section className="bg-cream border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="overline text-terracotta mb-4">Belong</p>
          <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter text-olive leading-[0.95] max-w-3xl">
            Become a <em className="italic font-light text-terracotta">member.</em>
          </h1>
          <p className="mt-6 text-ink/70 text-lg max-w-2xl">
            Membership at ETI is a covenant of faith and family. Tell us a little about yourself
            and we'll walk with you from here.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <form onSubmit={submit} className="space-y-6 bg-white border border-border p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="full_name">Full name *</Label>
              <Input id="full_name" required value={f.full_name} onChange={set("full_name")} data-testid="mb-name" className="mt-2 h-11" />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" required value={f.email} onChange={set("email")} data-testid="mb-email" className="mt-2 h-11" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input id="phone" required value={f.phone} onChange={set("phone")} data-testid="mb-phone" className="mt-2 h-11" />
            </div>
            <div>
              <Label htmlFor="date_of_birth">Date of birth</Label>
              <Input id="date_of_birth" type="date" value={f.date_of_birth} onChange={set("date_of_birth")} data-testid="mb-dob" className="mt-2 h-11" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Marital status</Label>
              <Select value={f.marital_status} onValueChange={set("marital_status")}>
                <SelectTrigger data-testid="mb-marital" className="mt-2 h-11">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {MARITAL.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="previous_church">Previous church (if any)</Label>
              <Input id="previous_church" value={f.previous_church} onChange={set("previous_church")} data-testid="mb-prevchurch" className="mt-2 h-11" />
            </div>
          </div>
          <div>
            <Label htmlFor="address">Address</Label>
            <Input id="address" value={f.address} onChange={set("address")} data-testid="mb-address" className="mt-2 h-11" />
          </div>
          <div>
            <Label htmlFor="salvation_testimony">Salvation testimony</Label>
            <Textarea id="salvation_testimony" rows={4} value={f.salvation_testimony} onChange={set("salvation_testimony")} data-testid="mb-testimony" className="mt-2" />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="membership-submit"
            className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none h-12 px-10 text-base font-semibold"
          >
            {loading ? "Submitting…" : "Submit Membership"}
          </Button>
        </form>
      </section>
    </div>
  );
}
